import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment";
import { DATE_FORMAT, DATE_TIME_FORMAT } from "utils/constants";

dayjs.extend(customParseFormat);

const DISPLAY_DATE_PATTERN = /^\d{2}\/\d{2}\/\d{4}$/;

export const isStrictDisplayDateString = (value, format = DATE_FORMAT) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!DISPLAY_DATE_PATTERN.test(trimmed)) return false;
  return dayjs(trimmed, format, true).isValid();
};

export const parseStrictDisplayDate = (value, format = DATE_FORMAT) => {
  if (!isStrictDisplayDateString(value, format)) return null;
  return dayjs(value.trim(), format, true);
};

/** Returns true when the date must not be selectable (Ant Design disabledDate). */
export const isDateDisabledByPickerRule = (date, disabledDate) => {
  if (!date || !dayjs(date).isValid() || typeof disabledDate !== "function") {
    return false;
  }
  return disabledDate(dayjs(date));
};

export const SINGLE_DAY_RANGE_PRESETS = [
  {
    label: "TODAY",
    value: dayjs(),
  },
  {
    label: "YESTERDAY",
    value: dayjs().subtract(1, "day"),
  },
  {
    label: "TOMORROW",
    value: dayjs().add(1, "day"),
  },
];

export const isMajor = dayjs().subtract(18, "years");

/** Acoris adherent / spouse can subscribe from 16 years old. */
export const ACORIS_MIN_AGE = 16;

/** Latest birth date for 16+ (civil year): 31/12 of (current year − 16). */
export const acorisMinBirthDateEndOfYear = dayjs()
  .year(dayjs().year() - ACORIS_MIN_AGE)
  .month(11)
  .date(31)
  .startOf("day");

export const hundredYearsAgoStart = dayjs()
  .subtract(100, "year")
  .startOf("year");

/** Latest allowed adult birth date (must be 18+). */
export const adultBirthDateDisabledDate = (current) =>
  !current ||
  current.isAfter(isMajor, "day") ||
  current.isBefore(hundredYearsAgoStart, "day");

export const notAfterTodayDisabledDate = (current) =>
  !current || current.isAfter(dayjs(), "day");

export const adultBirthDatePickerValue = dayjs().subtract(30, "years");

export const acorisAdultBirthDateDisabledDate = (current) =>
  !current ||
  current.isAfter(acorisMinBirthDateEndOfYear, "day") ||
  current.isBefore(hundredYearsAgoStart, "day");

export const isAcorisAdultBirthDateAllowed = (date) =>
  !!date &&
  dayjs(date).isValid() &&
  !acorisAdultBirthDateDisabledDate(dayjs(date));

export const twentySevenYearsAgoStart = dayjs()
  .subtract(27, "year")
  .startOf("year");

export const twentyFiveYearsAgoStart = dayjs()
  .subtract(25, "year")
  .startOf("year");

export const displayDate = ({
  date,
  format = DATE_TIME_FORMAT,
  defaultValue = "-",
}) =>
  date && moment(date).isValid() ? moment(date).format(format) : defaultValue;

/**
 * Signature date when it exists. Otherwise the last successful send
 * to the insurer — never treat that send as a signature.
 */
export const resolveDisplayedContractDate = (subscription) => {
  if (!subscription) return null;
  if (subscription.signedDate) return subscription.signedDate;
  const lastSent =
    subscription.externalManagement &&
    subscription.externalManagement.lastSentToManagement;
  if (lastSent) return lastSent;
  const sent =
    subscription.additionalInfo && subscription.additionalInfo.dateOfMembership;
  return sent || null;
};

export const getInitials = (firstname, lastname) => {
  if (!firstname || !lastname) return "";
  return `${firstname[0].toUpperCase()}${lastname[0].toUpperCase()}`;
};

/**
 * Postgres `timestamp without time zone` columns (e.g. `createdDate`) are
 * serialized by the API without a trailing `Z` or offset, which makes
 * `new Date(value)` interpret them as local time. Force UTC by appending
 * `Z` when the input is a timezone-less ISO-like string. Inputs that
 * already carry an offset (`Z`, `+02:00`) are left untouched.
 */
export const ensureUtcDate = (value) => {
  if (typeof value !== "string") return new Date(value);
  const hasTimezone = value.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(value);
  if (hasTimezone) return new Date(value);
  const isoLike = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(value);
  return new Date(isoLike ? `${value.replace(" ", "T")}Z` : value);
};

export const formatParisDateTime = (value) => {
  if (!value) return "-";
  const date = ensureUtcDate(value);
  if (Number.isNaN(date.getTime())) return "-";
  const parts = new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const part = (type) =>
    (parts.find((item) => item.type === type) || {}).value || "";
  return `${part("day")}/${part("month")}/${part("year")} ${part("hour")}:${part("minute")}`;
};

export const formatDate = (dateString) => {
  const date = ensureUtcDate(dateString);
  return date.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    timeZone: "Europe/Paris",
  });
};

export const relativeFormatDate = (dateStr) => {
  if (!dateStr) return "Date invalide";

  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "Date invalide";

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Aujourd'hui";
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return "Hier";
  }
  return date.toLocaleDateString("fr-FR");
};

export const formatTime = (timeStr) => {
  if (!timeStr) return "--:--";

  const date = new Date(timeStr);
  if (isNaN(date.getTime())) return "--:--";

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

export const groupLogsByDate = (logs) => {
  return logs.reduce((groups, log) => {
    const dateGroup = relativeFormatDate(log.createdDate || log.date || null);
    if (!groups[dateGroup]) {
      groups[dateGroup] = [];
    }
    groups[dateGroup].push(log);
    return groups;
  }, {});
};

export const calculateAge = (birthDate, effectiveDate) => {
  const referenceDate = moment(effectiveDate).startOf("year");
  const birthMoment = moment(birthDate).startOf("year");
  const age = referenceDate.diff(birthMoment, "years");

  return age;
};
